// Deployment Config admin — ISP identity, states of operation, contacts, and the
// Local GeoHub: a per-state × per-layer geodata matrix where every layer loads
// (and re-fires on failure) independently. Replicates the external GeoHub, local.
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import HostnameTls from '../components/HostnameTls';

type DeployCfg = {
  id: number;
  isp_name: string;
  isp_code: string;
  states_of_operation: string[];
  contact_email: string | null;
  contact_phone: string | null;
  officer_name: string | null;
  officer_title: string | null;
  contractor_address: string | null;
};

type Cell = { status: string; rows: number; error: string | null };
type Coverage = { layers: string[]; states: { state: string; cells: Record<string, Cell> }[] };

const ALL_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

const LAYER_LABEL: Record<string, string> = {
  buildings: 'Buildings', roads: 'Roads', railroads: 'Rail', waterways: 'Water',
  census: 'Census', soil: 'Soil', towers: 'Towers', fcc_broadband: 'FCC BB',
};

function fmt(n: number): string {
  if (!n) return '';
  if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(0) + 'K';
  return String(n);
}

export default function DeploymentConfigPage() {
  const qc = useQueryClient();
  const { data: cfg, isLoading } = useQuery<DeployCfg | null>({
    queryKey: ['deployment-config'],
    queryFn: () => api.get('/bead/config'),
  });
  const { data: coverage } = useQuery<Coverage>({
    queryKey: ['geodata-coverage'],
    queryFn: () => api.get('/geodata/coverage'),
    refetchInterval: 4000,
  });
  const loadMut = useMutation({
    mutationFn: (v: { state: string; layer?: string; layers?: string[] }) => api.post('/geodata/load', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['geodata-coverage'] }),
  });

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [states, setStates] = useState<string[]>([]);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [officerName, setOfficerName] = useState('');
  const [officerTitle, setOfficerTitle] = useState('');
  const [contractorAddress, setContractorAddress] = useState('');
  const [dirty, setDirty] = useState(false);
  const [initialized, setInit] = useState(false);

  useEffect(() => {
    if (cfg && !initialized) {
      setName(cfg.isp_name ?? ''); setCode(cfg.isp_code ?? '');
      setStates(cfg.states_of_operation ?? []);
      setEmail(cfg.contact_email ?? ''); setPhone(cfg.contact_phone ?? '');
      setOfficerName(cfg.officer_name ?? '');
      setOfficerTitle(cfg.officer_title ?? '');
      setContractorAddress(cfg.contractor_address ?? '');
      setInit(true);
    }
  }, [cfg, initialized]);

  const saveMut = useMutation({
    mutationFn: () => api.put('/bead/config', {
      isp_name: name, isp_code: code, states_of_operation: states,
      contact_email: email || null, contact_phone: phone || null,
      officer_name: officerName || null,
      officer_title: officerTitle || null,
      contractor_address: contractorAddress || null,
    }),
    onSuccess: () => { setDirty(false); qc.invalidateQueries({ queryKey: ['deployment-config'] }); },
  });

  if (isLoading) return <div className="card"><div className="card__body">Loading…</div></div>;
  if (!cfg) return <div className="card"><div className="card__body">No deployment_config row found.</div></div>;

  const toggleState = (s: string) => {
    setDirty(true);
    setStates((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s].sort());
  };

  const SECTION: React.CSSProperties = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, padding: '14px 18px', marginBottom: 16 };

  const layers = coverage?.layers ?? ['buildings','roads','railroads','waterways','census','soil','towers','fcc_broadband'];
  const covMap: Record<string, Record<string, Cell>> = {};
  (coverage?.states ?? []).forEach((s) => { covMap[s.state] = s.cells; });
  // rows = operating states, plus any loaded state not in the list
  const rowStates = Array.from(new Set([...states, ...Object.keys(covMap)])).sort();

  const cellStyle = (c?: Cell): React.CSSProperties => {
    const st = c?.status;
    let bg = '#f1f5f9', fg = '#94a3b8', bd = '#e2e8f0';
    if (st === 'done') { bg = '#dcfce7'; fg = '#047857'; bd = '#6ee7b7'; }
    else if (st === 'running' || st === 'queued') { bg = '#dbeafe'; fg = '#1d4ed8'; bd = '#93c5fd'; }
    else if (st === 'failed') { bg = '#fee2e2'; fg = '#b91c1c'; bd = '#fca5a5'; }
    return { background: bg, color: fg, border: '1px solid ' + bd, borderRadius: 5, padding: '4px 6px', fontSize: 11, fontWeight: 700, cursor: 'pointer', textAlign: 'center', whiteSpace: 'nowrap', minWidth: 60 };
  };
  const cellLabel = (c?: Cell): string => {
    if (!c) return '＋ load';
    if (c.status === 'done') return '✓ ' + (fmt(c.rows) || 'ok');
    if (c.status === 'running') return '⟳ loading';
    if (c.status === 'queued') return '… queued';
    if (c.status === 'failed') return '✗ retry';
    return c.status;
  };

  const inputStyle: React.CSSProperties = { width: 320, padding: '4px 8px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 3 };

  return (
    <div style={{ maxWidth: 1040 }}>
      <div className="page__header">
        <div>
          <h1 className="page__title">Deployment</h1>
          <div className="page__subtitle">ISP identity, operating states, and the Local GeoHub (per-state geodata) for this Milestone Build instance.</div>
        </div>
        <button onClick={() => saveMut.mutate()} disabled={!dirty || saveMut.isPending}
          style={{ padding: '8px 16px', background: dirty ? '#1e3a8a' : '#cbd5e1', color: '#fff', border: 'none', borderRadius: 5, fontWeight: 700, cursor: dirty ? 'pointer' : 'default' }}>
          {saveMut.isPending ? 'Saving…' : 'Save changes'}
        </button>
      </div>

      <div style={SECTION}>
        <h2 style={{ margin: 0, marginBottom: 8, fontSize: 14, fontWeight: 700 }}>ISP identity</h2>
        <table style={{ width: '100%', fontSize: 13 }}><tbody>
          <tr><td style={{ padding: '6px 8px', color: '#64748b', width: 180 }}>ISP name</td>
            <td style={{ padding: '6px 8px' }}><input value={name} onChange={(e) => { setName(e.target.value); setDirty(true); }} style={{ width: 320, padding: '4px 8px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 3 }} /></td></tr>
          <tr><td style={{ padding: '6px 8px', color: '#64748b' }}>ISP code (slug)</td>
            <td style={{ padding: '6px 8px' }}><input value={code} onChange={(e) => { setCode(e.target.value); setDirty(true); }} style={{ width: 200, padding: '4px 8px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 3 }} /></td></tr>
        </tbody></table>
      </div>

      <div style={SECTION}>
        <h2 style={{ margin: 0, marginBottom: 6, fontSize: 14, fontWeight: 700 }}>States of operation</h2>
        <div style={{ fontSize: 11, color: '#64748b', marginBottom: 10 }}>Restricts BEAD project queries, geodata coverage, and state filters to these states.</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: 4 }}>
          {ALL_STATES.map((s) => {
            const on = states.includes(s);
            return <button key={s} onClick={() => toggleState(s)} style={{ padding: '6px 0', fontSize: 11, fontWeight: 600, background: on ? '#1e3a8a' : '#f1f5f9', color: on ? '#fff' : '#475569', border: '1px solid ' + (on ? '#1e3a8a' : '#cbd5e1'), borderRadius: 3, cursor: 'pointer', fontVariantNumeric: 'tabular-nums' }}>{s}</button>;
          })}
        </div>
        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 8 }}>Selected: <strong>{states.length === 0 ? '(none)' : states.join(', ')}</strong></div>
      </div>

      <div style={SECTION}>
        <h2 style={{ margin: 0, marginBottom: 6, fontSize: 14, fontWeight: 700 }}>Local GeoHub — geodata coverage</h2>
        <div style={{ fontSize: 11, color: '#64748b', marginBottom: 12 }}>
          Every layer downloads onto THIS server per state (public Microsoft Building Footprints, Census TIGER, USDA SSURGO, FCC) — no external GeoHub. Each cell loads independently; click any cell to load or re-fire it if it failed. The design engine needs at least Buildings + Roads per state.
        </div>
        {rowStates.length === 0 ? (
          <div style={{ fontSize: 12, color: '#94a3b8' }}>Select states of operation above, then load their geodata here.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'separate', borderSpacing: 6, fontSize: 11 }}>
              <thead><tr>
                <th style={{ textAlign: 'left', color: '#64748b', padding: '0 6px' }}>State</th>
                {layers.map((l) => <th key={l} style={{ color: '#64748b', fontWeight: 700, padding: '0 4px' }}>{LAYER_LABEL[l] ?? l}</th>)}
                <th></th>
              </tr></thead>
              <tbody>
                {rowStates.map((st) => (
                  <tr key={st}>
                    <td style={{ fontWeight: 700, fontSize: 12, padding: '0 6px' }}>{st}</td>
                    {layers.map((l) => {
                      const c = covMap[st]?.[l];
                      return (
                        <td key={l}>
                          <div title={c?.error || c?.status || 'not loaded — click to load'} style={cellStyle(c)}
                            onClick={() => loadMut.mutate({ state: st, layer: l })}>
                            {cellLabel(c)}
                          </div>
                        </td>
                      );
                    })}
                    <td>
                      <button onClick={() => loadMut.mutate({ state: st, layer: 'all' })}
                        style={{ padding: '4px 8px', fontSize: 10, fontWeight: 700, background: '#1e3a8a', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        Load all
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 10 }}>
          ✓ loaded (row count) · ⟳ loading · ✗ failed (click to re-fire) · ＋ not loaded. Downloads run on the server; this refreshes every few seconds.
        </div>
      </div>

      <div style={SECTION}>
        <h2 style={{ margin: 0, marginBottom: 8, fontSize: 14, fontWeight: 700 }}>Contact</h2>
        <table style={{ width: '100%', fontSize: 13 }}><tbody>
          <tr><td style={{ padding: '6px 8px', color: '#64748b', width: 180 }}>Contact email</td>
            <td style={{ padding: '6px 8px' }}><input type="email" value={email} onChange={(e) => { setEmail(e.target.value); setDirty(true); }} placeholder="(optional)" style={{ width: 320, padding: '4px 8px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 3 }} /></td></tr>
          <tr><td style={{ padding: '6px 8px', color: '#64748b' }}>Contact phone</td>
            <td style={{ padding: '6px 8px' }}><input type="tel" value={phone} onChange={(e) => { setPhone(e.target.value); setDirty(true); }} placeholder="(optional)" style={{ width: 200, padding: '4px 8px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 3 }} /></td></tr>
        </tbody></table>
      </div>

      <div style={SECTION}>
        <h2 style={{ margin: 0, marginBottom: 4, fontSize: 14, fontWeight: 700 }}>Signing officer</h2>
        <div style={{ fontSize: 11, color: '#64748b', marginBottom: 10 }}>
          The authorized company officer who certifies federal payroll. These print in the signature block of the federal WH-347 certified-payroll report — leave them blank and the WH-347 prints unsigned.
        </div>
        <table style={{ width: '100%', fontSize: 13 }}><tbody>
          <tr><td style={{ padding: '6px 8px', color: '#64748b', width: 180 }}>Officer name</td>
            <td style={{ padding: '6px 8px' }}><input value={officerName} onChange={(e) => { setOfficerName(e.target.value); setDirty(true); }} placeholder="e.g. Jane Doe" style={inputStyle} /></td></tr>
          <tr><td style={{ padding: '6px 8px', color: '#64748b' }}>Officer title</td>
            <td style={{ padding: '6px 8px' }}><input value={officerTitle} onChange={(e) => { setOfficerTitle(e.target.value); setDirty(true); }} placeholder="e.g. Chief Financial Officer" style={inputStyle} /></td></tr>
          <tr><td style={{ padding: '6px 8px', color: '#64748b', verticalAlign: 'top' }}>Contractor address</td>
            <td style={{ padding: '6px 8px' }}><textarea value={contractorAddress} onChange={(e) => { setContractorAddress(e.target.value); setDirty(true); }} placeholder="Street, City, State ZIP" rows={2} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} /></td></tr>
        </tbody></table>
      </div>

      <HostnameTls />

      <div style={{ fontSize: 10, color: '#94a3b8', textAlign: 'right' }}>Last saved: {cfg ? new Date().toLocaleString() : '—'} · row id {cfg?.id}</div>
    </div>
  );
}
